/**
 * useSkillTools — fetch the active tool registry from the server.
 *
 * Powers the "?" capability popover and the read-only DebugInspector drawer.
 * Server-side RBAC filters out `category: 'admin'` tools for non-admin users,
 * so the frontend just renders whatever it receives.
 *
 * Cache indefinitely + invalidate-on-mutation per CLAUDE.md — the registry
 * only changes on deploy. The popover and the inspector share a single cache
 * entry via the `['v4-skills-tools']` key.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';

export const SkillToolSchema = z.object({
  name: z.string(),
  description: z.string(),
  userLabel: z.string(),
  userDescription: z.string(),
  category: z.enum(['compose', 'read', 'write', 'admin']),
});

export const SkillToolsResponseSchema = z.array(SkillToolSchema);

export type SkillTool = z.infer<typeof SkillToolSchema>;

export function useSkillTools(): UseQueryResult<SkillTool[]> {
  const { apiFetch } = useApi();
  return useQuery<SkillTool[]>({
    queryKey: ['v4-skills-tools'],
    queryFn: async () => {
      return fetchJson(apiFetch, SkillToolsResponseSchema, '/api/v4/skills/tools');
    },
    staleTime: Infinity,
  });
}
